import { useState } from 'react';
import { Outlet, NavLink } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Truck, Tire, ImageSquare, ChartBar, ClockCounterClockwise, SignOut, Book, List, CaretLeft, CaretRight, X } from '@phosphor-icons/react';
import { toast } from 'sonner';

export default function Layout() {
  const { profile, signOut } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);

  const handleSignOut = async () => {
    await signOut();
    toast.success('Berhasil keluar');
  };

  const navItems = [
    { to: '/dashboard', label: 'Dasbor', icon: ChartBar, testid: 'dashboard' },
    { to: '/trucks', label: 'Armada', icon: Truck, testid: 'trucks' },
    { to: '/inventory', label: 'Stok Ban', icon: Tire, testid: 'inventory' },
    { to: '/scrap', label: 'Galeri Rusak', icon: ImageSquare, testid: 'scrap' },
    { to: '/history', label: 'Riwayat', icon: ClockCounterClockwise, testid: 'history' },
    { to: '/tutorial', label: 'Cara Pakai', icon: Book, testid: 'tutorial' },
  ];

  const closeMobile = () => setMobileOpen(false);

  return (
    <div className="min-h-screen bg-white">
      {/* Mobile top bar */}
      <header className="lg:hidden sticky top-0 z-30 bg-white border-b border-zinc-200 flex items-center justify-between px-4 py-3" data-testid="mobile-topbar">
        <button onClick={() => setMobileOpen(true)} className="p-2 hover:bg-zinc-100 rounded-lg" data-testid="mobile-menu-button" aria-label="Buka menu">
          <List size={22} weight="bold" />
        </button>
        <div className="flex items-center gap-2">
          <img
            src="https://customer-assets.emergentagent.com/job_fleet-tire-hub/artifacts/v5lfjoov_Logo%20GTB.svg"
            alt="GTB Logo"
            className="h-7 w-auto"
            data-testid="gtb-logo-mobile"
          />
          <h1 className="text-base font-black uppercase tracking-widest text-zinc-900" style={{ fontFamily: 'Chivo' }}>
            GTB TIRE
          </h1>
        </div>
        <div className="w-10" />
      </header>

      <div className="flex">
        {/* Sidebar */}
        <aside
          className={`
            fixed lg:sticky top-0 left-0 z-40 h-screen flex-shrink-0
            bg-white border-r border-zinc-200 flex flex-col
            transition-all duration-200
            ${mobileOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0
            ${collapsed ? 'lg:w-20' : 'lg:w-64'} w-64
          `}
          data-testid="sidebar"
        >
          <div className={`p-4 border-b border-zinc-200 flex items-center ${collapsed ? 'lg:justify-center' : 'justify-between'}`}>
            {!collapsed && (
              <div className="flex items-center gap-3">
                <img
                  src="https://customer-assets.emergentagent.com/job_fleet-tire-hub/artifacts/v5lfjoov_Logo%20GTB.svg"
                  alt="GTB Logo"
                  className="h-10 w-auto"
                  data-testid="gtb-logo-sidebar"
                />
                <div>
                  <h1 className="text-xl font-black uppercase tracking-widest text-zinc-900" style={{ fontFamily: 'Chivo' }} data-testid="app-title">
                    GTB TIRE
                  </h1>
                  <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-zinc-500 mt-1">MANAGEMENT</p>
                </div>
              </div>
            )}
            {collapsed && (
              <img
                src="https://customer-assets.emergentagent.com/job_fleet-tire-hub/artifacts/v5lfjoov_Logo%20GTB.svg"
                alt="GTB Logo"
                className="hidden lg:block h-9 w-auto"
                data-testid="gtb-logo-collapsed"
              />
            )}
            {/* Mobile close */}
            <button onClick={closeMobile} className="lg:hidden p-2 hover:bg-zinc-100 rounded-lg" data-testid="mobile-close-button" aria-label="Tutup menu">
              <X size={18} weight="bold" />
            </button>
            {/* Desktop collapse */}
            <button onClick={() => setCollapsed(!collapsed)} className="hidden lg:flex p-2 hover:bg-zinc-100 rounded-lg" data-testid="collapse-button" aria-label={collapsed ? 'Perluas sidebar' : 'Tutup sidebar'}>
              {collapsed ? <CaretRight size={18} weight="bold" /> : <CaretLeft size={18} weight="bold" />}
            </button>
          </div>

          <nav className="flex-1 p-3 overflow-y-auto" data-testid="main-navigation">
            <div className="space-y-1">
              {navItems.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  onClick={closeMobile}
                  title={item.label}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-4 py-3 text-sm font-mono uppercase tracking-wider transition-colors rounded-lg ${
                      isActive ? 'bg-red-600 text-white shadow-sm' : 'text-zinc-700 hover:bg-zinc-100 hover:text-zinc-900'
                    } ${collapsed ? 'lg:justify-center lg:px-0' : ''}`
                  }
                  data-testid={`nav-${item.testid}`}
                >
                  <item.icon size={20} weight="bold" />
                  <span className={collapsed ? 'lg:hidden' : ''}>{item.label}</span>
                </NavLink>
              ))}
            </div>
          </nav>

          <div className={`p-4 border-t border-zinc-200 ${collapsed ? 'lg:hidden' : ''}`}>
            <div className="mb-3">
              <p className="text-xs font-mono uppercase tracking-[0.2em] text-zinc-500">Perusahaan</p>
              <p className="text-sm text-zinc-900 font-mono truncate" data-testid="company-name">{profile?.companies?.name || 'Memuat...'}</p>
            </div>
            <div className="mb-4">
              <p className="text-xs font-mono uppercase tracking-[0.2em] text-zinc-500">ID Perusahaan</p>
              <p className="text-sm text-zinc-900 font-mono truncate" data-testid="user-login-id">{profile?.login_id || '—'}</p>
            </div>
            <button onClick={handleSignOut}
              className="w-full flex items-center gap-2 px-4 py-2 text-xs font-mono uppercase tracking-wider text-zinc-700 hover:text-zinc-900 hover:bg-zinc-100 transition-colors border border-zinc-300 rounded-lg"
              data-testid="sign-out-button">
              <SignOut size={16} weight="bold" />
              Keluar
            </button>
          </div>

          {collapsed && (
            <div className="hidden lg:block p-3 border-t border-zinc-200">
              <button onClick={handleSignOut}
                title="Keluar"
                className="w-full flex items-center justify-center p-3 text-zinc-700 hover:text-zinc-900 hover:bg-zinc-100 transition-colors border border-zinc-300 rounded-lg">
                <SignOut size={16} weight="bold" />
              </button>
            </div>
          )}
        </aside>

        {/* Mobile overlay */}
        {mobileOpen && (
          <div className="lg:hidden fixed inset-0 z-30 bg-black/40" onClick={closeMobile} data-testid="mobile-overlay" />
        )}

        {/* Main content */}
        <main className="flex-1 overflow-auto bg-white min-w-0">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
